import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, CalendarDays, CalendarRange, FileEdit, CheckCircle2,
  Users, Package, UserCog, FilePlus2, IndianRupee, Wrench, Clock,
} from "lucide-react";
import api, { inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="card p-5 flex items-center gap-4" data-testid={testid}>
    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${accent}`}>
      <Icon className="h-6 w-6" />
    </div>
    <div>
      <div className="text-2xl font-head font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then(({ data }) => setS(data)); }, []);

  if (!s) return <div className="p-8 text-slate-500">Loading dashboard…</div>;

  const ws = s.workStatus || { pending: 0, active: 0, hold: 0, completed: 0 };
  const board = [
    { key: "pending", label: "Pending", dot: "🟡", value: ws.pending, accent: "bg-amber-100 text-amber-700" },
    { key: "active", label: "Active", dot: "🔵", value: ws.active, accent: "bg-blue-100 text-blue-700" },
    { key: "hold", label: "Hold", dot: "🟠", value: ws.hold, accent: "bg-orange-100 text-orange-700" },
    { key: "completed", label: "Completed", dot: "🟢", value: ws.completed, accent: "bg-green-100 text-green-700" },
  ];

  const cards = [
    { icon: FileText, label: "Total OA", value: s.totalOA, accent: "bg-slate-900 text-white", t: "stat-total-oa" },
    { icon: CalendarDays, label: "Today's OA", value: s.todayOA, accent: "bg-amber-100 text-amber-700", t: "stat-today-oa" },
    { icon: CalendarRange, label: "This Month", value: s.monthOA, accent: "bg-blue-100 text-blue-700", t: "stat-month-oa" },
    { icon: FileEdit, label: "Draft OA", value: s.draftOA, accent: "bg-slate-100 text-slate-600", t: "stat-draft-oa" },
    { icon: CheckCircle2, label: "Completed", value: s.completedOA, accent: "bg-green-100 text-green-700", t: "stat-completed-oa" },
    { icon: Users, label: "Customers", value: s.totalCustomers, accent: "bg-indigo-100 text-indigo-700", t: "stat-customers" },
    { icon: Package, label: "Products", value: s.totalProducts, accent: "bg-purple-100 text-purple-700", t: "stat-products" },
    ...(user.role === "admin" ? [{ icon: UserCog, label: "Employees", value: s.totalEmployees, accent: "bg-rose-100 text-rose-700", t: "stat-employees" }] : []),
  ];

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user.name}`}
        subtitle="Overview of your Order Acceptance activity"
        actions={<button className="btn-accent" onClick={() => navigate("/oa/new")} data-testid="dash-create-oa"><FilePlus2 className="h-4 w-4" /> Create OA</button>}
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h3 className="font-head font-semibold text-slate-900 mb-3">OA Work Status</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {board.map((b) => (
              <button key={b.key} onClick={() => navigate(`/oa?workStatus=${b.key}`)} data-testid={`status-board-${b.key}`}
                className="card p-5 flex items-center gap-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center text-xl ${b.accent}`}>{b.dot}</div>
                <div>
                  <div className="text-2xl font-head font-bold text-slate-900">{b.value}</div>
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{b.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <button onClick={() => navigate("/reports?type=pending_orders")} data-testid="dash-pending-tile"
          className="w-full card p-5 flex items-center justify-between text-left border-l-4 border-l-amber-400 hover:shadow-md hover:-translate-y-0.5 transition-all">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><Clock className="h-6 w-6" /></div>
            <div>
              <div className="text-2xl font-head font-bold text-slate-900">{s.pendingOrders ?? 0} Pending Orders</div>
              <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">Click to open Pending Order Report</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-head font-bold ${(s.oldestPendingDays ?? 0) > 30 ? "text-red-600" : "text-slate-900"}`} data-testid="dash-oldest-pending">{s.oldestPendingDays ?? 0}d</div>
            <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">Oldest Pending</div>
          </div>
        </button>

        <div>
          <h3 className="font-head font-semibold text-slate-900 mb-3">Operations</h3>
          {s.overdueDeliveries > 0 && (
            <button onClick={() => navigate("/records/transporter")} data-testid="overdue-alert"
              className="w-full mb-3 flex items-center gap-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium hover:bg-red-100 transition-colors">
              <span className="text-lg">⚠</span> {s.overdueDeliveries} overdue {s.overdueDeliveries > 1 ? "deliveries" : "delivery"} past expected date and not marked delivered — click to review Transporter records.
            </button>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: "Total Invoice", value: s.totalInvoice, accent: "bg-indigo-100 text-indigo-700", t: "op-total-invoice" },
              { label: "Pending Dispatch", value: s.pendingDispatch, accent: "bg-amber-100 text-amber-700", t: "op-pending-dispatch" },
              { label: "In Transit", value: s.inTransit, accent: "bg-blue-100 text-blue-700", t: "op-in-transit" },
              { label: "Delivered", value: s.delivered, accent: "bg-green-100 text-green-700", t: "op-delivered" },
              { label: "Install Pending", value: s.installationPending, accent: "bg-orange-100 text-orange-700", t: "op-install-pending" },
              { label: "Install Done", value: s.installationCompleted, accent: "bg-emerald-100 text-emerald-700", t: "op-install-done" },
            ].map((c) => (
              <div key={c.label} className="card p-4 text-center" data-testid={c.t}>
                <div className={`text-2xl font-head font-bold text-slate-900`}>{c.value ?? 0}</div>
                <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mt-1">{c.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-head font-semibold text-slate-900 mb-3">Installation Status</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { key: "pending", label: "Pending", accent: "bg-amber-100 text-amber-700" },
              { key: "in_progress", label: "In Progress", accent: "bg-blue-100 text-blue-700" },
              { key: "hold", label: "Hold", accent: "bg-orange-100 text-orange-700" },
              { key: "completed", label: "Completed", accent: "bg-emerald-100 text-emerald-700" },
            ].map((b) => (
              <button key={b.key} onClick={() => navigate(`/records/installer?status=${b.key}`)} data-testid={`installer-board-${b.key}`}
                className="card p-5 flex items-center gap-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center text-lg font-bold ${b.accent}`}><Wrench className="h-5 w-5" /></div>
                <div>
                  <div className="text-2xl font-head font-bold text-slate-900">{(s.installerStatus || {})[b.key] || 0}</div>
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{b.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => <Stat key={c.label} {...c} testid={c.t} />)}
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><IndianRupee className="h-4 w-4 text-amber-600" /> Recent Order Acceptances</h3>
            <span className="text-sm text-slate-500">Net value (all): <b className="text-slate-800">{inr(s.netTotal)}</b></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="th">OA No.</th><th className="th">Date</th><th className="th">Customer</th>
                  <th className="th">PO No.</th><th className="th">Net Total</th><th className="th">Created By</th><th className="th">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {s.recent.length === 0 && <tr><td className="td text-slate-400" colSpan={7}>No OA yet. Create your first one.</td></tr>}
                {s.recent.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/oa/${o.id}/preview`)} data-testid={`recent-oa-${o.oaNumber}`}>
                    <td className="td font-mono font-semibold text-slate-900">{o.oaNumber}</td>
                    <td className="td">{o.oaDate || o.createdAt?.slice(0, 10)}</td>
                    <td className="td">{o.customerName}</td>
                    <td className="td">{o.partyPONumber || "—"}</td>
                    <td className="td font-semibold">{inr(o.netTotal)}</td>
                    <td className="td">{o.createdByName}</td>
                    <td className="td"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
